import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, ArrowRight, CheckCircle2, AlertCircle, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealRegistrationDialog = ({ open, onOpenChange, portal = 'partner' }) => {
  const [form, setForm] = useState({
    customer_name: '',
    product: '',
    quantity: '',
    partner_company: '',
    partner_person_name: '',
    partner_email: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [dealCode, setDealCode] = useState('');
  const [serverMessage, setServerMessage] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setForm({
          customer_name: '', product: '', quantity: '',
          partner_company: '', partner_person_name: '', partner_email: '', notes: '',
        });
        setErrors({});
        setStatus('idle');
        setDealCode('');
        setServerMessage('');
        setCopied(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.customer_name.trim()) er.customer_name = 'Customer name is required';
    if (!form.product.trim()) er.product = 'Product is required';
    const qty = Number(form.quantity);
    if (!form.quantity.toString().trim()) er.quantity = 'Quantity is required';
    else if (!Number.isInteger(qty) || qty < 1) er.quantity = 'Enter a whole number (1 or more)';
    if (!form.partner_company.trim()) er.partner_company = 'Partner company is required';
    if (!form.partner_person_name.trim()) er.partner_person_name = 'Partner contact name is required';
    if (form.partner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.partner_email))
      er.partner_email = 'Enter a valid email or leave it blank';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('loading');
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        product: form.product.trim(),
        quantity: Number(form.quantity),
        partner_company: form.partner_company.trim(),
        partner_person_name: form.partner_person_name.trim(),
        portal,
      };
      if (form.partner_email.trim()) payload.partner_email = form.partner_email.trim().toLowerCase();
      if (form.notes.trim()) payload.notes = form.notes.trim();

      const res = await axios.post(`${API}/deals/register`, payload, { withCredentials: true });
      setDealCode(res.data.deal_code);
      setStatus('success');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setServerMessage(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
      setStatus('error');
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(dealCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[560px] p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
        <div className="p-7">
          {status === 'success' ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 rounded-full bg-blue-500/15 border border-blue-500/40 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-blue-400" />
              </div>
              <h2 className="mt-5 text-[22px] font-semibold tracking-tight">Deal registered for approval</h2>
              <p className="mt-2 text-[14px] text-neutral-400 leading-relaxed">
                Our sales team has been notified and will respond within 2 business days.
              </p>
              <div className="mt-5 mx-auto inline-flex items-center gap-3 px-4 py-2.5 rounded-lg border border-neutral-800 bg-neutral-900">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500">Deal code</span>
                <code className="font-mono text-[14px] text-blue-400">{dealCode}</code>
                <button onClick={copyCode} className="text-neutral-400 hover:text-white transition-colors" title="Copy">
                  <Copy className="w-3.5 h-3.5" />
                </button>
                {copied && <span className="text-[11px] text-blue-400">Copied</span>}
              </div>
              <div className="mt-6 flex justify-center gap-2">
                <Button onClick={() => onOpenChange(false)} className="h-10 px-5 rounded-full bg-white text-neutral-950 hover:bg-neutral-200 text-[14px] font-medium">
                  Close
                </Button>
                <Button
                  onClick={() => { setStatus('idle'); setDealCode(''); }}
                  variant="outline"
                  className="h-10 px-5 rounded-full border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium"
                >
                  Register another
                </Button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader className="space-y-2">
                <p className="text-[12px] uppercase tracking-[0.18em] text-blue-400">{portal === 'employee' ? 'Employee Portal' : 'Partner Portal'} · Deal Registration</p>
                <DialogTitle className="text-[22px] leading-tight font-semibold tracking-tight">
                  Start Deal Registration
                </DialogTitle>
                <DialogDescription className="text-[13.5px] text-neutral-400 leading-relaxed">
                  Submit the opportunity to lock in partner protection and pricing benefits. All fields marked * are required.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="dr-customer" className="text-[13px] text-neutral-300">Customer name *</Label>
                    <Input
                      id="dr-customer"
                      value={form.customer_name}
                      onChange={update('customer_name')}
                      placeholder="Acme Inc."
                      className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-10"
                    />
                    {errors.customer_name && <p className="text-[12px] text-red-400">{errors.customer_name}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="dr-product" className="text-[13px] text-neutral-300">Product *</Label>
                    <Input
                      id="dr-product"
                      value={form.product}
                      onChange={update('product')}
                      placeholder="MetaxuOne Platform · Enterprise"
                      className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-10"
                    />
                    {errors.product && <p className="text-[12px] text-red-400">{errors.product}</p>}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="dr-quantity" className="text-[13px] text-neutral-300">Quantity *</Label>
                  <Input
                    id="dr-quantity"
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={update('quantity')}
                    placeholder="50"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-10 max-w-[200px]"
                  />
                  {errors.quantity && <p className="text-[12px] text-red-400">{errors.quantity}</p>}
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="dr-partner-company" className="text-[13px] text-neutral-300">Partner company *</Label>
                    <Input
                      id="dr-partner-company"
                      value={form.partner_company}
                      onChange={update('partner_company')}
                      placeholder="Your company"
                      className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-10"
                    />
                    {errors.partner_company && <p className="text-[12px] text-red-400">{errors.partner_company}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="dr-partner-person" className="text-[13px] text-neutral-300">Partner contact name *</Label>
                    <Input
                      id="dr-partner-person"
                      value={form.partner_person_name}
                      onChange={update('partner_person_name')}
                      placeholder="Your name"
                      className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-10"
                    />
                    {errors.partner_person_name && <p className="text-[12px] text-red-400">{errors.partner_person_name}</p>}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="dr-partner-email" className="text-[13px] text-neutral-300">Partner email <span className="text-neutral-500">(optional, recommended)</span></Label>
                  <Input
                    id="dr-partner-email"
                    type="email"
                    value={form.partner_email}
                    onChange={update('partner_email')}
                    placeholder="you@yourcompany.com"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-10"
                  />
                  {errors.partner_email && <p className="text-[12px] text-red-400">{errors.partner_email}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="dr-notes" className="text-[13px] text-neutral-300">Notes <span className="text-neutral-500">(optional)</span></Label>
                  <Textarea
                    id="dr-notes"
                    value={form.notes}
                    onChange={update('notes')}
                    placeholder="Use case, timing, decision criteria, competitive context…"
                    rows={3}
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                  />
                </div>

                {status === 'error' && (
                  <div className="flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[13px] text-red-300 leading-snug">{serverMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full h-11 mt-2 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {status === 'loading' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting deal…</>
                  ) : (
                    <>Submit for approval <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>

                <p className="text-[11.5px] text-neutral-500 leading-snug">
                  By submitting, you confirm that the information provided is accurate. Approval is subject to MetaxuOne&rsquo;s Partner Program terms.
                </p>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DealRegistrationDialog;
